import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useReadOnly, ReadOnlyBanner } from "../hooks/useReadOnly";
import api from "../utils/api";
import { Plus, Copy, Trash2, Layers, ChevronRight, Download, Upload } from "lucide-react";

const STEP_LABELS = {
  format_text: "Format Text",
  format_number: "Format Number",
  format_date: "Format Date",
  regex_extract: "Regex Extract",
  regex_replace: "Regex Replace",
  find_replace: "Find & Replace",
  lookup: "Lookup",
  cleanup: "Cleanup",
  concat: "Concatenate",
  arithmetic: "Arithmetic",
  conditional: "Conditional",
  row_filter: "Row Filter",
};

const TYPE_COLORS = {
  format_text: "#5C7A5E",
  format_number: "#1E40AF",
  format_date: "#7C3AED",
  regex_extract: "#B45309",
  regex_replace: "#B45309",
  find_replace: "#0369A1",
  lookup: "#5B21B6",
  cleanup: "#9A3412",
  concat: "#065F46",
  arithmetic: "#0F172A",
  conditional: "#831843",
  row_filter: "#6B7280",
};

export default function TransformSetsPage() {
  const readOnly = useReadOnly();
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const importRef = useRef();

  const downloadJSON = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      alert("Export failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/transform-sets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${res.data.imported} transform set(s)`);
      load();
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      importRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/transform-sets/");
      setSets(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (ts) => {
    if (!window.confirm(`Delete "${ts.name}"? This cannot be undone.`)) return;
    setDeletingId(ts.id);
    try {
      await api.delete(`/transform-sets/${ts.id}`);
      setSets(prev => prev.filter(s => s.id !== ts.id));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopy = async (ts) => {
    setCopyingId(ts.id);
    try {
      const r = await api.post(`/transform-sets/${ts.id}/copy`);
      setSets(prev => [r.data, ...prev]);
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "860px", margin: "0 auto" }}>
        <ReadOnlyBanner what="transform sets" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.5rem", color: "var(--text-primary)", margin: 0 }}>
              Transform Sets
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>
              Define reusable pre-processing steps applied to raw file data before reconciliation matching.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {!readOnly && <input ref={importRef} type="file" accept=".json" style={{ display: "none" }} onChange={e => handleImport(e.target.files?.[0])} />}
            {!readOnly && (
              <button
                data-testid="ts-import-btn"
                className="btn-secondary"
                onClick={() => importRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem" }}
              >
                <Upload size={13} /> Import
              </button>
            )}
            <button
              data-testid="ts-export-all-btn"
              className="btn-secondary"
              onClick={() => downloadJSON("/transform-sets/export-all", "transform_sets_export.json")}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem" }}
            >
              <Download size={13} /> Export All
            </button>
            {!readOnly && (
              <button
                data-testid="new-transform-set-btn"
                className="btn-primary"
                onClick={() => navigate("/transform-sets/new")}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Plus size={15} /> New Transform Set
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
        ) : sets.length === 0 ? (
          <div className="recon-card" style={{ padding: "3rem", textAlign: "center" }}>
            <Layers size={36} style={{ color: "var(--text-muted)", marginBottom: "1rem" }} />
            <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 0.5rem" }}>No transform sets yet</p>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: "0 0 1.25rem" }}>
              Create a transform set to apply cleanup, formatting, regex extraction, and calculated fields to file data before reconciliation.
            </p>
            <button className="btn-primary" onClick={() => navigate("/transform-sets/new")}>
              <Plus size={14} style={{ marginRight: 6 }} /> Create your first Transform Set
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {sets.map(ts => (
              <div key={ts.id} data-testid={`ts-card-${ts.id}`} className="recon-card"
                style={{ padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--text-primary)" }}>{ts.name}</span>
                    <span style={{ fontSize: "0.72rem", background: "var(--bg-subtle)", color: "var(--text-muted)", padding: "0.125rem 0.5rem", borderRadius: "9999px" }}>
                      {(ts.steps || []).length} step{(ts.steps || []).length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {ts.description && (
                    <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0.2rem 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {ts.description}
                    </p>
                  )}
                  {(ts.steps || []).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.5rem" }}>
                      {ts.steps.slice(0, 6).map((s, i) => (
                        <span key={i} style={{
                          fontSize: "0.68rem", padding: "0.1rem 0.45rem", borderRadius: "9999px",
                          background: `${TYPE_COLORS[s.step_type] || "#666"}18`,
                          color: TYPE_COLORS[s.step_type] || "#666",
                          border: `1px solid ${TYPE_COLORS[s.step_type] || "#666"}40`,
                          fontWeight: 500,
                        }}>
                          {STEP_LABELS[s.step_type] || s.step_type}
                        </span>
                      ))}
                      {(ts.steps || []).length > 6 && (
                        <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", padding: "0.1rem 0.3rem" }}>
                          +{ts.steps.length - 6} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                  <button
                    data-testid={`ts-export-${ts.id}`}
                    title="Export this transform set"
                    onClick={() => downloadJSON(`/transform-sets/${ts.id}/export`, `transform_set_${ts.name.replace(/\s+/g, "_")}.json`)}
                    style={{ background: "none", border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.375rem 0.625rem", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem" }}
                  >
                    <Download size={13} /> Export
                  </button>
                  <button
                    data-testid={`ts-copy-${ts.id}`}
                    title="Copy transform set"
                    onClick={() => handleCopy(ts)}
                    disabled={copyingId === ts.id}
                    style={{ background: "none", border: "1px solid var(--border-default)", borderRadius: "0.375rem", padding: "0.375rem 0.625rem", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem" }}
                  >
                    <Copy size={13} /> {copyingId === ts.id ? "Copying…" : "Copy"}
                  </button>
                  <button
                    data-testid={`ts-edit-${ts.id}`}
                    onClick={() => navigate(`/transform-sets/${ts.id}`)}
                    style={{ background: "none", border: "1px solid var(--sage-border)", borderRadius: "0.375rem", padding: "0.375rem 0.625rem", cursor: "pointer", color: "var(--sage)", display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem" }}
                  >
                    Edit <ChevronRight size={13} />
                  </button>
                  <button
                    data-testid={`ts-delete-${ts.id}`}
                    onClick={() => handleDelete(ts)}
                    disabled={deletingId === ts.id}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: "0.375rem" }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
